import { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/react";
import {
  CreatorsApi,
  CREATOR_STATUSES,
  type CreatorBulkChanges,
} from "../api/creators";
import { OriginsApi, type OriginRow } from "../api/origins";
import { EventsApi, type EventRow } from "../api/events";
import ReasonField from "./reason/ReasonField";
import logger from "../analytics/logger";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  ids: string[];
  onSaved: () => void;
};

/** Bulk-edit a subset of fields across the currently selected creators. Each
 * field has a "Change" toggle; only toggled fields are sent, so untouched
 * fields are left exactly as-is on every creator. A reason is required (the
 * edit is audited, same as single-creator edit). */
export default function BulkEditModal({
  isOpen,
  onClose,
  ids,
  onSaved,
}: Props) {
  const toast = useToast();

  const [enStatus, setEnStatus] = useState(false);
  const [enOrigin, setEnOrigin] = useState(false);
  const [enName, setEnName] = useState(false);
  const [enEmail, setEnEmail] = useState(false);

  const [status, setStatus] = useState<string>("ACTIVE");
  // Origin attribution is a cascade: pick an Origin, then a specific Event within
  // it (mirrors the single-creator edit). The bulk change sends origin_event_id.
  const [origins, setOrigins] = useState<OriginRow[]>([]);
  const [originEvents, setOriginEvents] = useState<EventRow[]>([]);
  const [selectedOriginId, setSelectedOriginId] = useState<string>("");
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [nameSearchable, setNameSearchable] = useState(true);
  const [emailSearchable, setEmailSearchable] = useState(true);
  const [reason, setReason] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reasonInvalid, setReasonInvalid] = useState(false);
  const [prevOpen, setPrevOpen] = useState(isOpen);

  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (!isOpen) {
      setEnStatus(false);
      setEnOrigin(false);
      setEnName(false);
      setEnEmail(false);
      setStatus("ACTIVE");
      setSelectedOriginId("");
      setSelectedEventId("");
      setOriginEvents([]);
      setNameSearchable(true);
      setEmailSearchable(true);
      setReason("");
      setError(null);
      setReasonInvalid(false);
      setBusy(false);
    }
  }

  // Load the origin list once the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    OriginsApi.list({ page: 1, page_size: 200 })
      .then((r) => setOrigins(r.origins))
      .catch((e) => logger.error("bulk_edit_origins_failed", e));
  }, [isOpen]);

  // When an origin is selected, load its events and default the selection to the
  // origin's Default event (every attribution stays valid), keeping the current
  // pick if it still belongs to this origin.
  useEffect(() => {
    let active = true;
    void (async () => {
      if (!selectedOriginId) {
        setOriginEvents([]);
        return;
      }
      try {
        const r = await EventsApi.list({ page: 1, origin_id: selectedOriginId });
        if (!active) return;
        setOriginEvents(r.events);
        setSelectedEventId((cur) => {
          if (cur && r.events.some((ev) => ev.id === cur)) return cur;
          const def = r.events.find((ev) => ev.name === "Default");
          return def?.id ?? r.events[0]?.id ?? "";
        });
      } catch (e) {
        logger.error("bulk_edit_origin_events_failed", e);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedOriginId]);

  const anyEnabled = enStatus || enOrigin || enName || enEmail;
  // When editing origin, an origin AND event must be chosen (the event carries the
  // attribution). The event auto-fills to Default once an origin is picked.
  const originIncomplete = enOrigin && !selectedEventId;

  const buildChanges = (): CreatorBulkChanges => {
    const changes: CreatorBulkChanges = {};
    if (enStatus) changes.status = status;
    if (enOrigin && selectedEventId) changes.origin_event_id = selectedEventId;
    const pii: NonNullable<CreatorBulkChanges["pii"]> = {};
    if (enName) pii.name_searchable = nameSearchable;
    if (enEmail) pii.email_searchable = emailSearchable;
    if (Object.keys(pii).length > 0) changes.pii = pii;
    return changes;
  };

  const handleSave = async () => {
    setError(null);
    if (!reason.trim()) {
      setReasonInvalid(true);
      return;
    }
    if (!anyEnabled) return;
    if (originIncomplete) {
      setError("Select an origin and event, or uncheck Origin & Event.");
      return;
    }
    setBusy(true);
    try {
      const res = await CreatorsApi.bulkUpdate(ids, buildChanges(), {
        reason: reason.trim(),
      });
      const n = res.updated.length;
      toast({
        status: "success",
        title: `Updated ${n} creator${n === 1 ? "" : "s"}`,
        description:
          res.refused.length > 0
            ? `${res.refused.length} could not be updated (no longer exist).`
            : undefined,
      });
      onSaved();
      onClose();
    } catch (e) {
      logger.error("bulk_edit_creators_failed", e);
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      const code = err?.response?.data?.error;
      setError(code || err?.message || "Failed to update creators");
    } finally {
      setBusy(false);
    }
  };

  const rowProps = (enabled: boolean) => ({
    opacity: enabled ? 1 : 0.5,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => (busy ? null : onClose())}
      size="lg"
      closeOnOverlayClick={!busy}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Edit {ids.length} creator{ids.length === 1 ? "" : "s"}
        </ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
          )}
          <Text fontSize="sm" color="gray.600" mb={3}>
            Check a field to change it on all selected creators. Unchecked fields
            are left unchanged.
          </Text>
          <Stack spacing={4}>
            {/* Status */}
            <HStack align="center" spacing={3} {...rowProps(enStatus)}>
              <Checkbox
                isChecked={enStatus}
                onChange={(e) => setEnStatus(e.target.checked)}
                isDisabled={busy}
                flexShrink={0}
                w="160px"
              >
                Status
              </Checkbox>
              <Select
                size="sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                isDisabled={!enStatus || busy}
              >
                {CREATOR_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </HStack>

            {/* Origin + Event (cascade) */}
            <HStack align="center" spacing={3} {...rowProps(enOrigin)}>
              <Checkbox
                isChecked={enOrigin}
                onChange={(e) => setEnOrigin(e.target.checked)}
                isDisabled={busy}
                flexShrink={0}
                w="160px"
              >
                Origin &amp; Event
              </Checkbox>
              <Select
                size="sm"
                placeholder="Select origin"
                value={selectedOriginId}
                onChange={(e) => {
                  setSelectedOriginId(e.target.value);
                  setSelectedEventId("");
                }}
                isDisabled={!enOrigin || busy}
              >
                {origins.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.display_name ? `${o.display_name} (${o.slug})` : o.slug}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                placeholder="Select event"
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                isDisabled={!enOrigin || !selectedOriginId || busy}
              >
                {originEvents.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name ?? "(unnamed)"}
                  </option>
                ))}
              </Select>
            </HStack>

            {/* Name searchable */}
            <HStack align="center" spacing={3} {...rowProps(enName)}>
              <Checkbox
                isChecked={enName}
                onChange={(e) => setEnName(e.target.checked)}
                isDisabled={busy}
                flexShrink={0}
                w="160px"
              >
                Name searchable
              </Checkbox>
              <RadioGroup
                value={nameSearchable ? "on" : "off"}
                onChange={(v) => setNameSearchable(v === "on")}
                isDisabled={!enName || busy}
              >
                <HStack spacing={4}>
                  <Radio size="sm" value="on">
                    On
                  </Radio>
                  <Radio size="sm" value="off">
                    Off
                  </Radio>
                </HStack>
              </RadioGroup>
            </HStack>

            {/* Email searchable */}
            <HStack align="center" spacing={3} {...rowProps(enEmail)}>
              <Checkbox
                isChecked={enEmail}
                onChange={(e) => setEnEmail(e.target.checked)}
                isDisabled={busy}
                flexShrink={0}
                w="160px"
              >
                Email searchable
              </Checkbox>
              <RadioGroup
                value={emailSearchable ? "on" : "off"}
                onChange={(v) => setEmailSearchable(v === "on")}
                isDisabled={!enEmail || busy}
              >
                <HStack spacing={4}>
                  <Radio size="sm" value="on">
                    On
                  </Radio>
                  <Radio size="sm" value="off">
                    Off
                  </Radio>
                </HStack>
              </RadioGroup>
            </HStack>

            <Box pt={1}>
              <ReasonField
                value={reason}
                onChange={(v) => {
                  setReason(v);
                  if (v.trim()) setReasonInvalid(false);
                }}
                isInvalid={reasonInvalid}
              />
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={busy}
            isDisabled={!anyEnabled || !reason.trim() || originIncomplete}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
